import { useI18n, type MessageKey } from '../i18n';

// Home's two small charts (Fahad, 2 Nov; D110): how much of the PO value has been delivered (one
// ring) and where the open requests are (a donut by stage). Stages are ordered, so the donut uses
// one hue from light (early) to dark (late) rather than different colours; the legend names every
// stage with its count, so nothing depends on colour alone.

const R = 52;
const STROKE = 14;
const C = 2 * Math.PI * R;
/** Gap between donut segments, in px along the circle (the surface shows through). */
const GAP = 2;

export function DeliveredRing({
  pct,
  delivered,
  ordered,
}: {
  pct: number;
  delivered: string;
  ordered: string;
}) {
  const { t } = useI18n();
  const shown = Math.max(0, Math.min(100, Math.round(pct)));
  return (
    <section class="card glance-chart" aria-labelledby="glance-ring">
      <h3 id="glance-ring" class="glance-chart__title">
        {t('home.glance.deliveredTitle')}
      </h3>
      <div class="glance-chart__body">
        <svg
          class="ring"
          viewBox="0 0 128 128"
          role="img"
          aria-label={t('home.glance.deliveredAria', { n: String(shown) })}
        >
          <circle class="ring__track" cx="64" cy="64" r={R} stroke-width={STROKE} fill="none" />
          {shown > 0 && (
            <circle
              class="ring__value"
              cx="64"
              cy="64"
              r={R}
              stroke-width={STROKE}
              fill="none"
              stroke-linecap="round"
              stroke-dasharray={`${(C * shown) / 100} ${C}`}
              transform="rotate(-90 64 64)"
            />
          )}
          <text class="ring__label" x="64" y="64" text-anchor="middle" dominant-baseline="central">
            {shown}%
          </text>
        </svg>
        <dl class="glance-chart__facts">
          <div>
            <dt>{t('dash.tile.delivered')}</dt>
            <dd dir="ltr">{delivered}</dd>
          </div>
          <div>
            <dt>{t('dash.tile.po')}</dt>
            <dd dir="ltr">{ordered}</dd>
          </div>
        </dl>
      </div>
    </section>
  );
}

export function StageDonut({ stages }: { stages: { stage: string; count: number }[] }) {
  const { t } = useI18n();
  const total = stages.reduce((n, s) => n + s.count, 0);
  let offset = 0;
  const arcs = stages.map((s, i) => {
    const len = total ? (C * s.count) / total : 0;
    const arc = { ...s, i, start: offset, len };
    offset += len;
    return arc;
  });
  const visible = arcs.filter((a) => a.count > 0);
  const gap = visible.length > 1 ? GAP : 0;
  return (
    <section class="card glance-chart" aria-labelledby="glance-donut">
      <h3 id="glance-donut" class="glance-chart__title">
        {t('home.glance.stagesTitle')}
      </h3>
      <div class="glance-chart__body">
        <svg
          class="ring"
          viewBox="0 0 128 128"
          role="img"
          aria-label={t('home.glance.stagesAria', { n: String(total) })}
        >
          <circle class="ring__track" cx="64" cy="64" r={R} stroke-width={STROKE} fill="none" />
          {visible.map((a) => (
            <circle
              key={a.stage}
              class={`ring__seg ring__seg--${a.i}`}
              cx="64"
              cy="64"
              r={R}
              stroke-width={STROKE}
              fill="none"
              stroke-dasharray={`${Math.max(a.len - gap, 0.5)} ${C}`}
              stroke-dashoffset={-a.start}
              transform="rotate(-90 64 64)"
            >
              <title>
                {t(`dash.stage.${a.stage}` as MessageKey)}: {a.count}
              </title>
            </circle>
          ))}
          <text class="ring__label" x="64" y="58" text-anchor="middle" dominant-baseline="central">
            {total}
          </text>
          <text class="ring__sub" x="64" y="80" text-anchor="middle" dominant-baseline="central">
            {t('home.glance.openLabel')}
          </text>
        </svg>
        <ul class="glance-legend">
          {arcs.map((a) => (
            <li key={a.stage} class={a.count === 0 ? 'glance-legend__none' : undefined}>
              <span class={`glance-legend__key ring__seg--${a.i}`} aria-hidden="true" />
              <span>{t(`dash.stage.${a.stage}` as MessageKey)}</span>
              <span class="glance-legend__n">{a.count}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
